import { IconButton, Paragraph, TextFieldGroup } from "@smartsuite/react-ui/lib";
import { type IpAddress, ipv4Ipv6Regex } from "@smartsuite/types";
import React, { type FC, useState } from "react";
import * as yup from "yup";
import { FormFieldControl } from "../../../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../../../hooks/useFormField";
import type { FormFieldProps } from "../../../../../types";
import { applyYupDefaults, validateWithYup } from "../../../../../validator";
import type { IpAddressValue } from "../../../ip-address.config";
import { AddIPAddress } from "./components/AddIPAddress/AddIPAddress";

import "./IPAddressFieldControlMultiple.sass";
import { useNormalizedMultipleValue } from "../../../../../../hooks/useNormalizedMultipleValue";
import { $t } from "../../../../../../utils/intl";
import { validatorText } from "../../../../../validator.text";
import { inputStylesWhiteNew } from "../../../../fields.constants";

type IPAddressFieldControlMultipleProps = FormFieldProps;

export const IPAddressFieldControlMultiple: FC<IPAddressFieldControlMultipleProps> = ({
  label,
  name,
  field,
  formItem,
  required,
  caption,
  placeholder,
  readOnly,
}) => {
  const [enteredValue, setEnteredValue] = useState<string>("");

  const formField = useFormField<IpAddressValue>({
    name,
    validate: validateWithYup(
      applyYupDefaults(
        yup
          .array()
          .test("isValidEnteredValue", " ", (value) => {
            return enteredValue ? ipv4Ipv6Regex.test(enteredValue) : true;
          })
          .of(
            yup.object().test("isValidIpAddress", " ", (value) => {
              return value ? ipv4Ipv6Regex.test(value.address) : true;
            }),
          ),
        field,
        formItem,
      ),
    ),
  });

  // Both variants of an IP field store an array, so the single-to-multiple switch cannot leave a
  // scalar here today. Normalizing anyway keeps this control out of the BUG-21362 class: a scalar
  // from any future path would reach the unguarded .map below and be rejected by yup.array() on
  // submit, exactly as it was for the email, select and link fields.
  const addresses = useNormalizedMultipleValue<IpAddress>(formField.value, formField.onChange);

  const handleAddNewAddress = (value: string): void => {
    formField.onChange([...addresses, { address: value, country_code: "" }]);
  };

  const handleChangeValue = (index: number, value: string): void => {
    const newValue = [...addresses];
    newValue[index] = { address: value, country_code: "" };
    formField.onChange(newValue);
  };

  const handleDeleteAddress = (index: number): void => {
    const newValue = [...addresses];
    newValue.splice(index, 1);
    formField.onChange(newValue);
  };

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      label={label}
      state={formField.state}
      caption={caption}
      required={required}
      errorMessage={formField.errorMessage}
      readOnly={readOnly}
    >
      <div className="ip-address-field-control-multiple">
        {addresses.map((value, index) => {
          const isValueValid = ipv4Ipv6Regex.test(value.address);

          return (
            <div key={index}>
              <TextFieldGroup
                background="white-new"
                disabled={readOnly}
                readOnly={readOnly}
                inputStyles={inputStylesWhiteNew}
                name={formField.name}
                placeholder={placeholder}
                required={required}
                value={value?.address}
                onBlur={formField.onBlur}
                onChange={(event) => handleChangeValue(index, event.target.value)}
                onFocus={formField.onFocus}
              >
                <IconButton
                  className="phone-field-control-multiple__delete_button"
                  name="close"
                  ariaLabel="delete"
                  onClick={() => handleDeleteAddress(index)}
                />
              </TextFieldGroup>

              {!isValueValid && (
                <div className="ip-address-field-control-multiple__error-message">
                  <Paragraph color="error" size="s">
                    {$t(validatorText.validationIPAddress)}
                  </Paragraph>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <AddIPAddress
        readOnly={readOnly}
        onAddNewAddress={handleAddNewAddress}
        placeholder={placeholder}
        forceAdding={!addresses.length}
        onChangeEnteredValue={setEnteredValue}
      />
    </FormFieldControl>
  );
};
